import React, { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, Loader } from 'lucide-react'
import { supabase } from '../config/supabase'

const IrmaChat = () => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '¡Hola! Soy Irma, tu asistente de gimnasta. Puedo ayudarte con entrenamiento, técnicas, lesiones, nutrición y más. ¿En qué puedo ayudarte hoy?' }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => {
      setCooldown(prev => Math.max(0, prev - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  const sendMessage = async () => {
    if (!input.trim() || loading || cooldown > 0) return

    const userMessage = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setLoading(true)
    setError('')

    try {
      const { data, error: fnError } = await supabase.functions.invoke('irma-chat', {
        body: {
          message: userMessage,
          history: messages.slice(-10)
        }
      })

      if (fnError) throw fnError

      if (data?.cooldown) {
        setCooldown(data.retryAfter || 300)
        setError(data.error || 'Too many requests')
        setMessages(prev => prev.slice(0, -1))
      } else if (data?.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }])
        if (data.provider) {
          console.log(`Irma respondió usando: ${data.provider}`)
        }
      } else if (data?.error) {
        throw new Error(data.error)
      }
    } catch (err) {
      console.error('Chat error:', err)
      setError(err.message || 'Error al conectar con Irma')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      overflow: 'hidden'
    }}>
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        {messages.map((msg, i) => (
          <div 
            key={i}
            style={{
              display: 'flex',
              gap: 8,
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%'
            }}
          >
            {msg.role === 'assistant' && <Bot size={16} style={{ marginTop: 4 }} />}
            <div style={{
              padding: '8px 12px',
              borderRadius: 12,
              background: msg.role === 'user' ? '#10b981' : '#f3f4f6',
              color: msg.role === 'user' ? 'white' : '#1f2937',
              fontSize: '0.9rem',
              whiteSpace: 'pre-wrap'
            }}>
              {msg.content}
            </div>
            {msg.role === 'user' && <User size={16} style={{ marginTop: 4 }} />}
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Loader size={16} className="spin" />
            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Irma está escribiendo...</span>
          </div>
        )}

        {cooldown > 0 && (
          <div style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600 }}>
            Espera {cooldown}seg antes de enviar otro mensaje...
          </div>
        )}

        {error && !cooldown && (
          <div style={{ color: '#ef4444', fontSize: '0.85rem' }}>
            Error: {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div style={{ 
        padding: 12, 
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        gap: 8
      }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe tu mensaje..."
          disabled={loading || cooldown > 0}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 20,
            outline: 'none',
            fontSize: '0.9rem',
            background: cooldown > 0 ? '#f3f4f6' : 'white'
          }}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim() || cooldown > 0}
          style={{
            padding: '8px 16px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: 20,
            cursor: loading || !input.trim() || cooldown > 0 ? 'not-allowed' : 'pointer',
            opacity: loading || !input.trim() || cooldown > 0 ? 0.6 : 1
          }}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}

export default IrmaChat